import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import {
  Lock, Crown, ArrowLeft, Settings, ExternalLink, Sparkles, User as UserIcon,
} from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import NotificationSettings from '@/components/NotificationSettings';
import { NewsletterPreferencesCard } from '@/components/NewsletterPreferencesCard';
import FirstLoginNotificationsModal from '@/components/FirstLoginNotificationsModal';
import ConnectedAccounts from '@/components/account/ConnectedAccounts';
import IapManageBanner from '@/components/account/IapManageBanner';
import DisplaySettingsCard from '@/components/settings/DisplaySettingsCard';

const TIER_NAMES = {
  individual: 'Individual ($1.99/mo)',
  family: 'Family & Friends ($2.99/mo)',
  cookout: 'The Whole Cookout ($5.99/mo)',
  reunion: 'Family Reunion ($12.99/mo)',
};

const getSubscriptionDisplay = (user, subscriptionStatus) => {
  if (user.is_admin) return { label: 'Admin', badge: 'destructive' };
  if (user.is_gifted) return { label: 'Gifted (Free VIP)', badge: 'default' };
  if (subscriptionStatus?.status === 'trial') return { label: '7-Day Trial', badge: 'secondary' };
  if (subscriptionStatus?.status === 'active' && subscriptionStatus?.tier) {
    return { label: TIER_NAMES[subscriptionStatus.tier] || subscriptionStatus.tier_name, badge: 'default' };
  }
  if (subscriptionStatus?.status === 'expired') return { label: 'Expired', badge: 'outline' };
  if (subscriptionStatus?.status === 'cancelled') return { label: 'Cancelled', badge: 'outline' };
  return { label: 'Free (Limited Access)', badge: 'outline' };
};

/**
 * SettingsPage (/settings) — private configuration.
 * Order:
 *   1. VIP / IAP banners
 *   2. Subscription
 *   3. Display (theme, language, opening screen, timezone)
 *   4. Notifications (push + email, side-by-side)
 *   5. Newsletter Preferences
 *   6. Connected Accounts + Password (combined)
 *   7. Sign Out
 */
const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const { isSpanish, toggleLanguage } = useLanguage();

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'system');
  const [defaultLanguage, setDefaultLanguage] = useState(() => localStorage.getItem('defaultLanguage') || 'en');
  const [openingScreen, setOpeningScreen] = useState(() => localStorage.getItem('clearcomm-opening-screen') || 'library');
  const [userTimezone, setUserTimezone] = useState('');
  const [isSavingTimezone, setIsSavingTimezone] = useState(false);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [newPasswordOnly, setNewPasswordOnly] = useState({ password: '', confirmPassword: '' });

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const { data } = await apiFetch('/api/auth/preferences');
        if (data?.preferences) {
          const p = data.preferences;
          if (p.theme) { setTheme(p.theme); localStorage.setItem('theme', p.theme); }
          if (p.language) { setDefaultLanguage(p.language); localStorage.setItem('defaultLanguage', p.language); }
          if (p.opening_screen) { setOpeningScreen(p.opening_screen); localStorage.setItem('clearcomm-opening-screen', p.opening_screen); }
        }
      } catch (prefsErr) {
        // Falling back to localStorage defaults is the expected behavior
        // when the server is unreachable; just log for diagnostics.
        console.debug('[settings] preference load failed, using localStorage:', prefsErr);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    const loadTimezone = async () => {
      if (!isAuthenticated) return;
      try {
        const { data, error } = await apiFetch('/api/subscription/status');
        if (!error && data) setUserTimezone(data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
        else setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      } catch { setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone); }
    };
    loadTimezone();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Apply theme to document root + persist
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else if (theme === 'light') root.classList.remove('dark');
    else {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) root.classList.add('dark');
      else root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('defaultLanguage', defaultLanguage);
    if ((defaultLanguage === 'es' && !isSpanish) || (defaultLanguage === 'en' && isSpanish)) toggleLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLanguage]);

  const syncPreference = async (prefs) => {
    if (!isAuthenticated) return;
    try { await apiFetch('/api/auth/preferences', { method: 'PUT', body: prefs }); }
    catch { toast.error(isSpanish ? 'Error al guardar preferencias' : 'Failed to save preference'); }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    syncPreference({ theme: newTheme });
    toast.success(isSpanish
      ? `Tema cambiado a ${newTheme === 'dark' ? 'oscuro' : newTheme === 'light' ? 'claro' : 'sistema'}`
      : `Theme changed to ${newTheme}`);
  };

  const handleLanguageChange = (lang) => {
    setDefaultLanguage(lang);
    syncPreference({ language: lang });
    toast.success(lang === 'es' ? '¡Idioma cambiado a Español!' : 'Language changed to English!');
  };

  const handleOpeningScreenChange = (val) => {
    setOpeningScreen(val);
    localStorage.setItem('clearcomm-opening-screen', val);
    syncPreference({ opening_screen: val });
    const labels = isSpanish
      ? { home: 'Inicio', library: 'Biblioteca', todo: 'Tareas', timer: 'Temporizador', blog: 'Blog', interpreter: 'Intérprete' }
      : { home: 'Home', library: 'Library', todo: 'To-Do List', timer: 'Timer', blog: 'Blog', interpreter: 'Interpreter' };
    toast.success((isSpanish ? 'Pantalla de inicio: ' : 'Opening screen: ') + (labels[val] || val));
  };

  const handleTimezoneChange = async (newTimezone) => {
    setIsSavingTimezone(true);
    try {
      const { error } = await apiFetch('/api/subscription/set-timezone', { method: 'POST', body: { timezone: newTimezone } });
      if (!error) { setUserTimezone(newTimezone); toast.success(isSpanish ? 'Zona horaria actualizada' : 'Timezone updated'); }
      else toast.error(error || 'Failed to update timezone');
    } catch { toast.error('Something went wrong'); }
    finally { setIsSavingTimezone(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) { toast.error('New passwords do not match'); return; }
    if (passwordForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setIsChangingPassword(true);
    try {
      const { error } = await apiFetch('/api/auth/change-password', {
        method: 'POST',
        body: { current_password: passwordForm.currentPassword, new_password: passwordForm.newPassword },
      });
      if (!error) {
        toast.success('Password changed successfully');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else { toast.error(error || 'Failed to change password'); }
    } catch { toast.error('Something went wrong'); }
    finally { setIsChangingPassword(false); }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (newPasswordOnly.password !== newPasswordOnly.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPasswordOnly.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setIsSettingPassword(true);
    try {
      const { error } = await apiFetch('/api/auth/set-password', { method: 'POST', body: { new_password: newPasswordOnly.password } });
      if (!error) {
        toast.success('Password set! You can now log in with email and password.');
        setNewPasswordOnly({ password: '', confirmPassword: '' });
      } else { toast.error(error || 'Failed to set password'); }
    } catch { toast.error('Something went wrong'); }
    finally { setIsSettingPassword(false); }
  };

  const handleLogout = async () => {
    await logout(); navigate('/'); toast.success('Logged out successfully');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }
  if (!user) return null;

  // OAuth-only users (signed up via Google/Facebook/Apple without setting a
  // password). Show prominent "Set password" CTA so they can also log in via email.
  const isOAuthOnly = !!user.auth_provider && user.auth_provider !== 'email' && !user.password_hash;
  const subscriptionDisplay = getSubscriptionDisplay(user, subscriptionStatus);
  const isPremium = subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trial' || user.is_gifted || user.is_admin;

  // Password section — embedded inside the ConnectedAccounts card
  const passwordSection = (
    <div className="pt-2">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="h-4 w-4 text-muted-foreground" />
        <h4 className="font-semibold text-sm">
          {isOAuthOnly
            ? (isSpanish ? 'Configurar contraseña' : 'Set Password')
            : (isSpanish ? 'Cambiar contraseña' : 'Change Password')}
        </h4>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        {isOAuthOnly
          ? (isSpanish ? 'Establece una contraseña para iniciar sesión también con email' : 'Set a password to also log in with email')
          : (isSpanish ? 'Actualiza tu contraseña' : 'Update your account password')}
      </p>
      {isOAuthOnly ? (
        <form onSubmit={handleSetPassword} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-password-oauth" className="text-xs">{isSpanish ? 'Nueva Contraseña' : 'New Password'}</Label>
            <Input
              id="new-password-oauth" type="password" placeholder="••••••••"
              value={newPasswordOnly.password}
              onChange={(e) => setNewPasswordOnly({ ...newPasswordOnly, password: e.target.value })}
              required minLength={6} data-testid="new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-new-password-oauth" className="text-xs">{isSpanish ? 'Confirmar Contraseña' : 'Confirm Password'}</Label>
            <Input
              id="confirm-new-password-oauth" type="password" placeholder="••••••••"
              value={newPasswordOnly.confirmPassword}
              onChange={(e) => setNewPasswordOnly({ ...newPasswordOnly, confirmPassword: e.target.value })}
              required data-testid="confirm-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={isSettingPassword}
            className="bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-semibold"
            data-testid="set-password-btn"
          >
            {isSettingPassword ? (isSpanish ? 'Configurando...' : 'Setting Password...') : (isSpanish ? 'Configurar Contraseña' : 'Set Password')}
          </Button>
        </form>
      ) : (
        <form onSubmit={handleChangePassword} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="current-password" className="text-xs">{isSpanish ? 'Contraseña Actual' : 'Current Password'}</Label>
            <Input
              id="current-password" type="password" placeholder="••••••••"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              required data-testid="current-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password" className="text-xs">{isSpanish ? 'Nueva Contraseña' : 'New Password'}</Label>
            <Input
              id="new-password" type="password" placeholder="••••••••"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              required minLength={6} data-testid="new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password" className="text-xs">{isSpanish ? 'Confirmar Nueva Contraseña' : 'Confirm New Password'}</Label>
            <Input
              id="confirm-password" type="password" placeholder="••••••••"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              required data-testid="confirm-password-input"
            />
          </div>
          <Button type="submit" disabled={isChangingPassword} data-testid="change-password-btn">
            {isChangingPassword ? (isSpanish ? 'Cambiando...' : 'Changing Password...') : (isSpanish ? 'Cambiar Contraseña' : 'Change Password')}
          </Button>
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <FirstLoginNotificationsModal />

      <div className="container py-8 max-w-2xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-button">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {isSpanish ? 'Atrás' : 'Back'}
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold">{isSpanish ? 'Configuración' : 'Settings'}</h1>
          <Link to="/profile" className="text-sm text-[#00A896] hover:underline inline-flex items-center gap-1" data-testid="link-to-profile">
            <UserIcon className="h-4 w-4" />
            {isSpanish ? 'Mi Perfil' : 'My Profile'}
          </Link>
        </div>

        {/* VIP Trial Banner for Free Users */}
        {!isPremium && (
          <Card className="mb-6 bg-gradient-to-r from-[#00E5CC] to-purple-500 text-[#0d1f2d] border-0" data-testid="vip-trial-banner">
            <CardContent className="py-6">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Sparkles className="h-12 w-12 flex-shrink-0" />
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-xl font-bold mb-1">{isSpanish ? '¡Prueba VIP Gratis por 7 Días!' : 'Try VIP Free for 7 Days!'}</h3>
                  <p className="text-white/90 text-sm">{isSpanish ? 'Acceso ilimitado a más de 3,400 frases.' : 'Unlimited access to 3,400+ phrases.'}</p>
                </div>
                <Button onClick={() => navigate('/subscription')} className="bg-white text-purple-600 hover:bg-white/90 font-bold" data-testid="start-trial-btn">
                  <Crown className="h-4 w-4 mr-2" />{isSpanish ? 'Comenzar Prueba' : 'Start Free Trial'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* OAuth-only set-password CTA pinned at top */}
        {isOAuthOnly && (
          <Card className="mb-6 border-2 border-[#00E5CC]/40 bg-gradient-to-br from-[#00E5CC]/5 to-purple-500/5" data-testid="oauth-set-password-cta">
            <CardContent className="py-4 flex items-center gap-4">
              <Lock className="h-6 w-6 text-[#00A896] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{isSpanish ? 'Añade una contraseña' : 'Add a password'}</p>
                <p className="text-xs text-muted-foreground">
                  {isSpanish
                    ? `Inicias sesión solo con ${user.auth_provider}. Establece una contraseña para iniciar sesión también con email.`
                    : `You sign in with ${user.auth_provider} only. Set a password to also log in with email.`}
                </p>
              </div>
              <a href="#password-card" className="text-xs font-semibold text-[#00A896] hover:underline whitespace-nowrap" data-testid="jump-to-password">
                {isSpanish ? 'Establecer abajo →' : 'Set below →'}
              </a>
            </CardContent>
          </Card>
        )}

        {/* IAP-only manage banner */}
        <IapManageBanner user={user} isSpanish={isSpanish} />

        {/* 1. Subscription */}
        <Card className="mb-6" data-testid="subscription-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Crown className="h-5 w-5 text-yellow-500" />{isSpanish ? 'Suscripción' : 'Subscription'}</CardTitle>
            <CardDescription>{isSpanish ? 'Administra tu acceso VIP' : 'Manage your VIP access'}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-muted-foreground text-sm">{isSpanish ? 'Plan Actual' : 'Current Plan'}</p>
                <Badge variant={subscriptionDisplay.badge} data-testid="subscription-badge" className="mt-1">
                  {isPremium && <Crown className="h-3 w-3 mr-1" />}
                  {subscriptionDisplay.label}
                </Badge>
              </div>
              {subscriptionStatus?.trial_ends_at && subscriptionStatus?.status === 'trial' && (
                <div className="text-right">
                  <p className="text-muted-foreground text-xs">{isSpanish ? 'Prueba termina' : 'Trial ends'}</p>
                  <p className="text-sm font-medium">{new Date(subscriptionStatus.trial_ends_at).toLocaleDateString()}</p>
                </div>
              )}
              {subscriptionStatus?.subscription_ends_at && subscriptionStatus?.status === 'active' && (
                <div className="text-right">
                  <p className="text-muted-foreground text-xs">{isSpanish ? 'Se renueva' : 'Renews'}</p>
                  <p className="text-sm font-medium">{new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString()}</p>
                </div>
              )}
            </div>
            <Separator />
            <Button variant="outline" onClick={() => navigate('/subscription')} className="w-full" data-testid="manage-subscription-btn">
              <Settings className="h-4 w-4 mr-2" />{isSpanish ? 'Administrar Suscripción' : 'Manage Subscription'}
              <ExternalLink className="h-3 w-3 ml-auto" />
            </Button>
          </CardContent>
        </Card>

        {/* 2. Display (theme/language/opening screen/timezone) */}
        <DisplaySettingsCard
          isSpanish={isSpanish}
          theme={theme}
          onThemeChange={handleThemeChange}
          defaultLanguage={defaultLanguage}
          onLanguageChange={handleLanguageChange}
          openingScreen={openingScreen}
          onOpeningScreenChange={handleOpeningScreenChange}
          userTimezone={userTimezone}
          onTimezoneChange={handleTimezoneChange}
          isSavingTimezone={isSavingTimezone}
        />

        {/* 3. Notifications (push + email, side-by-side) */}
        <div className="mb-6"><NotificationSettings /></div>

        {/* 4. Newsletter Preferences (topic-level opt-ins) */}
        <NewsletterPreferencesCard email={user?.email} isSpanish={isSpanish} />

        {/* 5. Connected Accounts + inline Password form */}
        <ConnectedAccounts isSpanish={isSpanish}>
          {passwordSection}
        </ConnectedAccounts>

        {/* 6. Logout */}
        <Card className="border-orange-200" data-testid="logout-card">
          <CardHeader><CardTitle className="text-orange-600">{isSpanish ? 'Hasta Luego Cocodrilo' : 'See You Later Alligator'}</CardTitle></CardHeader>
          <CardContent>
            <Button variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50" onClick={handleLogout} data-testid="logout-btn">
              {isSpanish ? 'Cerrar Sesión' : 'Sign Out'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
